import { getData } from "@/lib/prismic/data";
import { InspirationCategoryDocument, InspirationDocument } from "@/prismicio-types";
import { use } from "react";
import StageHeader from "@/components/organisms/stage-header";
import { SliceZone } from "@prismicio/react";
import { components } from "@/slices";
import OurServices from "@/components/organisms/our-services";
import { getAllByType } from "@/lib/prismic";
import { filter } from "@prismicio/client";
import InspirationMasonaryGrid from "@/components/organisms/inspiraion-masonary-grid";
import InspirationFilter from "@/components/molecules/inspiration-filter";
import { notFound } from "next/navigation";
import { generateSEO } from "@/helpers/seo";
import { resolveInspirationsWithProducts } from "@/lib/prismic/inspirations";
import { InspirationCategoryWithTranslation } from "@/types";

type InspirationCategoryProps = {
  resolution: Resolution;
};

const InspirationCategory = ({ resolution }: InspirationCategoryProps) => {
  const inspirationCategory = use(getData(resolution)) as InspirationCategoryDocument | null;

  if (!inspirationCategory || !inspirationCategory.data) return notFound();

  const inspirationCategories = use(
    getAllByType("inspiration_category", resolution.locale, {
      fetch: ["inspiration_category.title", "inspiration_category.featuredImage"],
    })
  ) as InspirationCategoryDocument[] | null;

  const inspirations = use(
    getAllByType("inspiration", resolution.locale, {
      filters: [filter.at("my.inspiration.categories.inspirationCategory", inspirationCategory.id)],
    })
  ) as InspirationDocument[] | null;

  if (!inspirationCategories || !inspirations || inspirations.length === 0) return notFound();

  const inspirationsWithProducts = use(resolveInspirationsWithProducts(inspirations, resolution.locale));

  const { longDescription } = inspirationCategory.data;

  const inspirationCategoryWithTranslation = inspirationCategory as InspirationCategoryWithTranslation;

  let featuredImage;

  if (resolution.locale === "fr") {
    featuredImage = inspirationCategoryWithTranslation.data.featuredImage;
  } else {
    featuredImage = inspirationCategoryWithTranslation.translation.data.featuredImage;
  }

  return (
    <>
      <StageHeader
        locale={resolution.locale}
        className="container my-6 md:my-4"
        orientationOnMobile="center"
        hideDescriptionOnMobile
        isDescriptionCentered={false}
        description={longDescription}
        imageAlt={featuredImage.alt || ""}
        imageUrl={featuredImage.url || ""}
      />
      <InspirationFilter
        currentCategoryUid={inspirationCategory.uid}
        inspirationCategories={inspirationCategories}
        locale={resolution.locale}
      />
      <InspirationMasonaryGrid
        locale={resolution.locale}
        inspirations={inspirationsWithProducts.sort((a, b) => {
          return (a.data.display || 9999) - (b.data.display || 9999);
        })}
      />
      <article className="container my-10 max-lg:hidden">
        <SliceZone
          components={components}
          slices={inspirationCategory.data.slices}
          context={{ richtextVariant: "richtext" }}
        />
      </article>
      <OurServices locale={resolution.locale} />
    </>
  );
};

export default InspirationCategory;

InspirationCategory.generateMetadata = async (resolution: Resolution) => {
  const inspirationCategory = (await getData(resolution)) as InspirationCategoryDocument | null;

  if (!inspirationCategory || !inspirationCategory.data) return {};

  return generateSEO(inspirationCategory, resolution.locale);
};
